import UserService from "@/services/user-service";
import {lbFetchRaw} from "@/extensions";

type TrackingDefinition = {
    code: string
    route?: string | null
    matchMode: 'prefix' | 'exact'
    usageType: string
    debounceSeconds: number
    interactionKeys?: string[]
    interactionLabels?: Record<string, string>
}

const STORAGE_KEY = 'lb-feature-usage-tracker'

class FeatureUsageTracker {
    private registry: TrackingDefinition[] = []
    private registryPromise: Promise<TrackingDefinition[]> | null = null
    private inFlightKeys = new Set<string>()
    private sentAtByKey: Record<string, number> = this.restoreSentAt()

    install(router: any) {
        if (this.shouldDisableTracking()) {
            return
        }

        router.isReady().then(() => {
            this.trackRoute(router.currentRoute.value)
        })

        router.afterEach((to: any) => {
            this.trackRoute(to)
        })
    }

    private async trackRoute(route: any) {
        if (this.shouldDisableTracking()) {
            return
        }

        const currentUser = UserService.getCurrentUser()?.jwtUser
        const path = this.normalizeRoute(route?.path)

        if (!currentUser?.tenantId || !path || this.shouldSkip(path)) {
            return
        }

        const registry = await this.loadRegistry()
        if (!registry.length) {
            return
        }

        const matches = registry.filter((definition) => !!definition.route && this.matches(definition, path))
        for (const definition of matches) {
            await this.sendTrack({
                featureCode: definition.code,
                route: path,
                usageType: definition.usageType,
                source: 'frontend'
            }, `${definition.code}:${path}`, definition.debounceSeconds || 900)
        }
    }

    async trackInteraction(
        featureCode: string,
        eventKey: string,
        metadata: Record<string, any> = {},
        incrementBy: number = 1,
        dimension: { key?: string | null, label?: string | null } = {}
    ) {
        if (this.shouldDisableTracking()) {
            return
        }

        const currentUser = UserService.getCurrentUser()?.jwtUser
        if (!currentUser?.tenantId || !featureCode || !eventKey) {
            return
        }

        const registry = await this.loadRegistry()
        const definition = registry.find((entry) => entry.code === featureCode)
        if (!definition?.interactionKeys?.includes(eventKey)) {
            return
        }

        await this.sendTrack({
            featureCode,
            eventKey,
            incrementBy,
            route: this.getCurrentRoute(),
            usageType: 'interaction',
            source: 'frontend',
            dimensionKey: dimension.key ?? null,
            dimensionLabel: dimension.label ?? null,
            metadata
        })
    }

    private shouldSkip(path: string) {
        return path === '/'
            || path.startsWith('/login')
            || path.startsWith('/logout')
            || path.startsWith('/operator')
    }

    private shouldDisableTracking() {
        if (typeof window === 'undefined') {
            return false
        }

        return !!(window as Window & { Cypress?: unknown }).Cypress
    }

    private async loadRegistry(): Promise<TrackingDefinition[]> {
        if (this.shouldDisableTracking()) {
            return []
        }

        if (this.registry.length > 0) {
            return this.registry
        }

        if (!this.registryPromise) {
            this.registryPromise = lbFetchRaw({
                url: '/api/feature-catalog/tracking-registry',
                method: 'GET',
                headers: {}
            })
                .then(async (response) => {
                    if (!response.ok) {
                        const body = await response.text().catch(() => '')
                        throw new Error(`Failed to load tracking registry: ${response.status} ${body}`)
                    }

                    return response.json()
                }, undefined)
                .then((definitions: TrackingDefinition[]) => {
                    this.registry = Array.isArray(definitions) ? definitions : []
                    return this.registry
                })
                .catch((error) => {
                    console.warn('[FeatureUsageTracker] Could not load registry', error)
                    this.registryPromise = null
                    return []
                })
        }

        return this.registryPromise
    }

    private async sendTrack(
        payload: Record<string, any>,
        dedupeKey: string | null = null,
        debounceSeconds: number = 0
    ) {
        if (this.shouldDisableTracking()) {
            return
        }

        const now = Date.now()

        if (dedupeKey) {
            const lastSentAt = this.sentAtByKey[dedupeKey] || 0
            if ((now - lastSentAt) < debounceSeconds * 1000) {
                return
            }
        }

        const requestKey = dedupeKey || `${payload.featureCode}:${payload.eventKey || payload.route}:${now}`
        if (this.inFlightKeys.has(requestKey)) {
            return
        }

        this.inFlightKeys.add(requestKey)

        try {
            const response = await lbFetchRaw({
                url: '/api/feature-usage/track',
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                data: payload
            })

            if (!response.ok) {
                const body = await response.text().catch(() => '')
                console.warn('[FeatureUsageTracker] Tracking failed', response.status, body)
                return
            }

            if (dedupeKey) {
                this.sentAtByKey[dedupeKey] = now
                this.persistSentAt()
            }
        } catch (error) {
            console.warn('[FeatureUsageTracker] Could not send usage event', error)
        } finally {
            this.inFlightKeys.delete(requestKey)
        }
    }

    private matches(definition: TrackingDefinition, path: string) {
        const route = this.normalizeRoute(definition.route)
        if (!route) {
            return false
        }

        if (definition.matchMode === 'exact') {
            return path === route
        }

        return path === route || path.startsWith(`${route}/`)
    }

    private normalizeRoute(route: string | null | undefined) {
        if (!route) {
            return ''
        }

        const normalized = route
            .trim()
            .replace(/^#/, '')
            .split('?')[0]
            .split('#')[0]
            .replace(/\/+$/, '')

        if (!normalized) {
            return ''
        }

        return normalized.startsWith('/') ? normalized : `/${normalized}`
    }

    private getCurrentRoute() {
        const hashRoute = window.location.hash?.replace(/^#/, '') || ''
        return this.normalizeRoute(hashRoute)
    }

    private restoreSentAt(): Record<string, number> {
        try {
            const value = window.sessionStorage.getItem(STORAGE_KEY)
            if (!value) {
                return {}
            }

            const parsed = JSON.parse(value)
            return typeof parsed === 'object' && parsed ? parsed : {}
        } catch (_) {
            return {}
        }
    }

    private persistSentAt() {
        try {
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.sentAtByKey))
        } catch (_) {
            // ignore storage failures
        }
    }
}

const featureUsageTracker = new FeatureUsageTracker()

export default featureUsageTracker
